import React, { useState, useEffect, useCallback } from 'react';
import { workOrdersAPI, machinesAPI, reportsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Plus, Search, Calendar, ClipboardList, Settings, Play, CheckCircle, 
  Clock, AlertTriangle, ChevronLeft, ChevronRight, Printer, FileSpreadsheet,
  Check, Eye, EyeOff, Package, Download, Loader2
} from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  released: 'bg-blue-100 text-blue-800',
  wip: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  on_hold: 'bg-orange-100 text-orange-800',
  cancelled: 'bg-red-100 text-red-800',
};

const PRIORITY_LABELS = {
  1: { label: 'Critical', color: 'bg-red-500 text-white' },
  2: { label: 'Normal', color: 'bg-blue-500 text-white' },
  3: { label: 'Low', color: 'bg-gray-500 text-white' },
};

const SUPPLY_CONDITIONS = ['As Drawn', 'Annealed', 'Spheroidized', 'Phosphate Coated'];
const TOOL_STATUS_OPTIONS = ['Available', 'To Make', 'In Production', 'Under Repair'];

export default function ProductionPlanningPage() {
  const [activeTab, setActiveTab] = useState('backlog');
  const [backlog, setBacklog] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [machines, setMachines] = useState([]);
  const [planningData, setPlanningData] = useState({ swimlanes: [], unassigned: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);  // Hide completed by default
  const [updatingWoId, setUpdatingWoId] = useState(null);  // Track which WO is being updated
  
  // Create WO Dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [selectedBacklogItem, setSelectedBacklogItem] = useState(null);
  const [woForm, setWoForm] = useState({
    qty_target: 0,
    assigned_machine_id: '',
    planned_start_date: '',
    planned_end_date: '',
    rm_grade: '',
    rm_size: '',
    wire_size: '',
    rm_qty_required: '',
    supply_condition: '',
    header_tool_status: '',
    heat_treatment: '',
    surface_finish: '',
    priority: 2,
    production_notes: '',
  });
  const [creating, setCreating] = useState(false);
  
  // Gantt state
  const [viewStartDate, setViewStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay()); // Start of week
    return d.toISOString().split('T')[0];
  });
  const [daysToShow] = useState(14); // 2 weeks

  const { isAdmin } = useAuth();

  const fetchBacklog = useCallback(async () => {
    try {
      const res = await workOrdersAPI.getBacklog();
      setBacklog(res.data.items || []);
    } catch (error) {
      toast.error('Failed to fetch backlog');
    }
  }, []);

  const fetchWorkOrders = useCallback(async () => {
    try {
      const res = await workOrdersAPI.list({ limit: 200 });
      setWorkOrders(res.data.data || res.data.items || []);
    } catch (error) {
      toast.error('Failed to fetch work orders');
    }
  }, []);

  const fetchMachines = useCallback(async () => {
    try {
      const res = await machinesAPI.list({ is_active: true });
      setMachines(res.data || []);
    } catch (error) {
      toast.error('Failed to fetch machines');
    }
  }, []);

  const fetchPlanningData = useCallback(async () => {
    try {
      const res = await workOrdersAPI.getPlanning();
      setPlanningData(res.data || { swimlanes: [], unassigned: [] });
    } catch (error) {
      toast.error('Failed to fetch planning data');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchBacklog(), fetchWorkOrders(), fetchMachines(), fetchPlanningData()]);
      setLoading(false);
    };
    loadData();
  }, [fetchBacklog, fetchWorkOrders, fetchMachines, fetchPlanningData]);

  const openCreateDialog = (item) => {
    setSelectedBacklogItem(item);
    setWoForm({
      qty_target: item.remaining_qty,
      assigned_machine_id: '',
      planned_start_date: '',
      planned_end_date: '',
      rm_grade: '',
      rm_size: '',
      wire_size: '',
      rm_qty_required: '',
      supply_condition: '',
      header_tool_status: '',
      heat_treatment: '',
      surface_finish: '',
      priority: 2,
      production_notes: '',
    });
    setCreateDialog(true);
  };

  // Handle machine selection - auto-populate planned_start_date
  const handleMachineChange = async (machineId) => {
    setWoForm(prev => ({ ...prev, assigned_machine_id: machineId }));
    
    if (machineId) {
      try {
        const res = await machinesAPI.getNextAvailableDate(machineId);
        const nextDate = res.data.next_available_date;
        const machineNameInfo = res.data.machine_name;
        const woCount = res.data.active_work_orders_count;
        
        // Auto-populate the planned_start_date
        setWoForm(prev => ({ ...prev, planned_start_date: nextDate }));
        
        // Show toast with scheduling info
        if (woCount > 0) {
          toast.info(
            `${machineNameInfo}: Next available ${nextDate} (${woCount} active WO${woCount > 1 ? 's' : ''})`,
            { duration: 4000 }
          );
        } else {
          toast.success(`${machineNameInfo}: Available immediately`, { duration: 3000 });
        }
      } catch (error) {
        // Don't show error toast - just log it, user can still enter date manually
      }
    }
  };

  // Inline machine assignment for existing WOs (especially manual/unassigned)
  const handleInlineMachineAssign = async (wo, newMachineId) => {
    if (!newMachineId) return;
    
    setUpdatingWoId(wo.id);
    try {
      // Get next available date for the new machine
      const scheduleRes = await machinesAPI.getNextAvailableDate(newMachineId);
      const nextDate = scheduleRes.data.next_available_date;
      
      // Update WO with new machine and scheduled start date
      const updateData = {
        assigned_machine_id: newMachineId,
        planned_start_date: nextDate,
      };
      
      await workOrdersAPI.update(wo.id, updateData);
      
      const machineName = machines.find(m => m.id === newMachineId)?.name || 'Machine';
      toast.success(`${wo.wo_number} assigned to ${machineName}, scheduled from ${nextDate}`);
      
      // Refresh data
      fetchWorkOrders();
      fetchPlanningData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign machine');
    } finally {
      setUpdatingWoId(null);
    }
  };

  const handleCreateWO = async () => {
    if (!selectedBacklogItem) return;
    if (woForm.qty_target <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }
    
    setCreating(true);
    try {
      const data = {
        sales_order_id: selectedBacklogItem.order_id,
        sales_order_item_id: selectedBacklogItem.item_id,
        qty_target: woForm.qty_target,
        assigned_machine_id: woForm.assigned_machine_id || null,
        planned_start_date: woForm.planned_start_date || null,
        planned_end_date: woForm.planned_end_date || null,
        rm_grade: woForm.rm_grade || null,
        rm_size: woForm.rm_size || null,
        wire_size: woForm.wire_size || null,
        rm_qty_required: woForm.rm_qty_required ? parseFloat(woForm.rm_qty_required) : null,
        supply_condition: woForm.supply_condition || null,
        header_tool_status: woForm.header_tool_status || null,
        heat_treatment: woForm.heat_treatment || null,
        surface_finish: woForm.surface_finish || null,
        priority: woForm.priority,
        production_notes: woForm.production_notes || null,
      };
      
      const res = await workOrdersAPI.create(data);
      toast.success(`Work Order ${res.data.wo_number} created!`);
      setCreateDialog(false);
      fetchBacklog();
      fetchWorkOrders();
      fetchPlanningData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Work Order');
    } finally {
      setCreating(false);
    }
  };

  const handleReleaseWO = async (wo) => {
    try {
      await workOrdersAPI.release(wo.id);
      toast.success(`${wo.wo_number} released for production`);
      fetchWorkOrders();
      fetchPlanningData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release Work Order');
    }
  };

  const handleStartWO = async (wo) => {
    try {
      await workOrdersAPI.start(wo.id);
      toast.success(`${wo.wo_number} started - WIP`);
      fetchWorkOrders();
      fetchPlanningData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Work Order');
    }
  };

  const handleCompleteWO = async (wo) => {
    try {
      await workOrdersAPI.complete(wo.id);
      toast.success(`${wo.wo_number} marked as Complete!`);
      fetchWorkOrders();
      fetchPlanningData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete Work Order');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount || 0);
  };

  // CSV Export for Work Orders (Legacy ERP Bridge)
  const exportWorkOrdersToCSV = async () => {
    try {
      // Fetch work orders (max 200 per request per backend limit)
      const res = await workOrdersAPI.list({ limit: 200 });
      const allWOs = res.data?.data || res.data?.items || [];
      
      // CSV Header as per requirements
      const headers = ['WO No', 'Parent SO No', 'Machine (M/C)', 'Plan Date', 'Target Delivery Date', 'Item (Part No)', 'RM Grade', 'Wire Size', 'Quantity', 'Status'];
      
      // Build CSV rows
      const rows = allWOs.map(wo => [
        wo.wo_number || '',
        wo.order_number || wo.sales_order_number || '',
        wo.machine_name || 'Unassigned',
        wo.planned_start_date ? new Date(wo.planned_start_date).toLocaleDateString('en-IN') : '-',
        wo.target_delivery_date || wo.planned_end_date ? new Date(wo.target_delivery_date || wo.planned_end_date).toLocaleDateString('en-IN') : '-',
        wo.part_internal_no || '',
        wo.rm_grade || '-',
        wo.wire_size || '-',
        (wo.qty_target || 0).toString(),
        wo.status || 'draft'
      ]);
      
      // Generate CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `work_orders_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      
      toast.success(`Exported ${rows.length} work orders to CSV`);
    } catch (error) {
      toast.error('Failed to export work orders');
    }
  };

  // Generate date columns for Gantt
  const generateDateColumns = () => {
    const columns = [];
    const start = new Date(viewStartDate);
    for (let i = 0; i < daysToShow; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      columns.push({
        date: d.toISOString().split('T')[0],
        dayName: d.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: d.getDate(),
        isWeekend: d.getDay() === 0 || d.getDay() === 6,
        isToday: d.toDateString() === new Date().toDateString(),
      });
    }
    return columns;
  };

  const dateColumns = generateDateColumns();

  const navigateWeek = (direction) => {
    const d = new Date(viewStartDate);
    d.setDate(d.getDate() + (direction * 7));
    setViewStartDate(d.toISOString().split('T')[0]);
  };

  // Calculate WO bar position for Gantt
  // FIXED: Handle missing/invalid dates by defaulting to "Today" with 1-day duration
  const getWOBarStyle = (wo) => {
    const viewStart = new Date(viewStartDate);
    viewStart.setHours(0, 0, 0, 0);
    const viewEnd = new Date(viewStart);
    viewEnd.setDate(viewEnd.getDate() + daysToShow);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Default to today if no start date (fallback for visibility)
    let woStart = today;
    if (wo.planned_start_date) {
      woStart = new Date(wo.planned_start_date);
      if (isNaN(woStart.getTime())) {
        woStart = today; // Invalid date - fallback
      }
    }
    
    // Default to start + 1 day if no end date
    let woEnd = new Date(woStart);
    woEnd.setDate(woEnd.getDate() + 1);
    if (wo.planned_end_date) {
      const parsedEnd = new Date(wo.planned_end_date);
      if (!isNaN(parsedEnd.getTime())) {
        woEnd = parsedEnd;
      }
    }
    
    // Check if WO falls entirely outside the view range
    if (woEnd < viewStart || woStart > viewEnd) {
      return null;
    }
    
    const dayWidth = 100 / daysToShow;
    const startOffset = Math.max(0, (woStart - viewStart) / (1000 * 60 * 60 * 24));
    const duration = Math.max(1, (woEnd - woStart) / (1000 * 60 * 60 * 24) + 1);
    
    // Flag to indicate if this WO has missing dates (for UI indicator)
    const hasMissingDates = !wo.planned_start_date || !wo.planned_end_date;
    
    return {
      left: `${startOffset * dayWidth}%`,
      width: `${Math.min(duration, daysToShow - startOffset) * dayWidth}%`,
      hasMissingDates,
    };
  };

  const filteredBacklog = backlog.filter(item => 
    !search || 
    item.part_internal_no?.toLowerCase().includes(search.toLowerCase()) ||
    item.order_number?.toLowerCase().includes(search.toLowerCase()) ||
    item.customer_name?.toLowerCase().includes(search.toLowerCase())
  );

  // Filter work orders - hide completed unless showCompleted is true
  const filteredWorkOrders = workOrders.filter(wo => {
    // Status filter
    if (!showCompleted && wo.status === 'completed') return false;
    // Search filter
    if (search && !(
      wo.wo_number?.toLowerCase().includes(search.toLowerCase()) ||
      wo.part_internal_no?.toLowerCase().includes(search.toLowerCase()) ||
      wo.customer_name?.toLowerCase().includes(search.toLowerCase())
    )) return false;
    return true;
  });

  // Filter planning swimlanes - hide completed jobs
  const filteredPlanningData = {
    ...planningData,
    swimlanes: planningData.swimlanes?.map(lane => ({
      ...lane,
      work_orders: (lane.work_orders || []).filter(wo => 
        showCompleted || wo.status !== 'completed'
      )
    })) || [],
    unassigned: (planningData.unassigned || []).filter(wo =>
      showCompleted || wo.status !== 'completed'
    )
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary">Production Planning</h1>
          <p className="text-muted-foreground">Manage work orders and production schedule</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportWorkOrdersToCSV} data-testid="export-wo-csv-btn">
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Export to CSV
          </Button>
          <Button variant="outline" onClick={() => window.open('/planning/print', '_blank')}>
            <Printer className="h-4 w-4 mr-2" /> Print Planning Sheet
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 max-w-lg">
          <TabsTrigger value="backlog" className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" /> Backlog
          </TabsTrigger>
          <TabsTrigger value="planning" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" /> Planning Board
          </TabsTrigger>
          <TabsTrigger value="work-orders" className="flex items-center gap-2">
            <Settings className="h-4 w-4" /> Work Orders
          </TabsTrigger>
          <TabsTrigger value="materials" className="flex items-center gap-2">
            <Package className="h-4 w-4" /> Material Planning
          </TabsTrigger>
        </TabsList>

        {/* Backlog Tab */}
        <TabsContent value="backlog" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" /> Production Backlog
              </CardTitle>
              <CardDescription>Unplanned Sales Order items awaiting Work Order creation</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by part, order, customer..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading backlog...</div>
              ) : filteredBacklog.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No unplanned items. All Sales Orders have been scheduled!
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order #</TableHead>
                      <TableHead>Part No</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead className="text-right">Total Qty</TableHead>
                      <TableHead className="text-right">Planned</TableHead>
                      <TableHead className="text-right">Remaining</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBacklog.map((item) => (
                      <TableRow key={`${item.order_id}-${item.item_id}`}>
                        <TableCell className="font-mono font-medium">{item.order_number}</TableCell>
                        <TableCell className="font-mono">{item.part_internal_no}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{item.description}</TableCell>
                        <TableCell>{item.customer_name}</TableCell>
                        <TableCell className="text-right font-mono">{item.total_qty?.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">{item.planned_qty?.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-mono font-medium text-primary">{item.remaining_qty?.toLocaleString()}</TableCell>
                        <TableCell>
                          <Button size="sm" onClick={() => openCreateDialog(item)}>
                            <Plus className="h-4 w-4 mr-1" /> Create WO
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Planning Board Tab (Gantt) */}
        <TabsContent value="planning" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" /> Planning Board
                  </CardTitle>
                  <CardDescription>Visual timeline view - Swimlanes per Machine</CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  {/* Show Completed Toggle */}
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="showCompleted" 
                      checked={showCompleted} 
                      onCheckedChange={setShowCompleted}
                    />
                    <Label htmlFor="showCompleted" className="text-sm cursor-pointer flex items-center gap-1">
                      {showCompleted ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      Show Completed
                    </Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium px-2">
                      {new Date(viewStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - 
                      {new Date(new Date(viewStartDate).setDate(new Date(viewStartDate).getDate() + daysToShow - 1)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setViewStartDate(new Date().toISOString().split('T')[0])}>
                      Today
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <div className="min-w-[1000px]">
                  {/* Date Header */}
                  <div className="flex border-b">
                    <div className="w-48 flex-shrink-0 p-2 font-medium bg-muted">Machine</div>
                    <div className="flex-1 flex">
                      {dateColumns.map((col) => (
                        <div
                          key={col.date}
                          className={`flex-1 text-center p-1 text-xs border-l ${col.isWeekend ? 'bg-gray-100' : ''} ${col.isToday ? 'bg-primary/10' : ''}`}
                        >
                          <div className="font-medium">{col.dayName}</div>
                          <div>{col.dayNum}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Swimlanes */}
                  {filteredPlanningData.swimlanes?.map((lane) => (
                    <div key={lane.machine_id} className="flex border-b hover:bg-muted/50">
                      <div className="w-48 flex-shrink-0 p-2 border-r bg-muted/30">
                        <div className="font-medium text-sm">{lane.machine_name}</div>
                        <div className="text-xs text-muted-foreground">{lane.process_type}</div>
                      </div>
                      <div className="flex-1 relative h-16">
                        {lane.work_orders?.map((wo) => {
                          const barStyle = getWOBarStyle(wo);
                          if (!barStyle) return null;
                          
                          // Check if delivery will be late
                          const isDeliveryLate = wo.target_delivery_date && wo.planned_end_date && 
                            new Date(wo.planned_end_date) > new Date(wo.target_delivery_date);
                          
                          return (
                            <div
                              key={wo.id}
                              className={`absolute top-2 h-12 rounded px-2 py-1 text-xs cursor-pointer overflow-hidden ${
                                barStyle.hasMissingDates ? 'border-2 border-dashed border-red-400 ' : ''
                              }${
                                isDeliveryLate ? 'ring-2 ring-red-600 ring-offset-1 ' : ''
                              }${
                                wo.status === 'completed' ? 'bg-green-500 text-white' :
                                wo.status === 'wip' ? 'bg-yellow-500 text-black' :
                                wo.status === 'released' ? 'bg-blue-500 text-white' :
                                'bg-gray-400 text-white'
                              }`}
                              style={{ left: barStyle.left, width: barStyle.width }}
                              title={`${wo.wo_number}: ${wo.part_internal_no} (${wo.qty_target} pcs)${barStyle.hasMissingDates ? ' - DATES MISSING' : ''}${isDeliveryLate ? ' ⚠️ LATE DELIVERY' : ''}`}
                              onClick={() => {
                                // Quick actions via click
                                if (wo.status === 'draft') handleReleaseWO(wo);
                                else if (wo.status === 'released') handleStartWO(wo);
                                else if (wo.status === 'wip') handleCompleteWO(wo);
                              }}
                            >
                              <div className="flex items-center gap-1">
                                {isDeliveryLate && <AlertTriangle className="h-3 w-3 text-red-200 flex-shrink-0" />}
                                <span className="font-medium truncate">{wo.wo_number}</span>
                              </div>
                              <div className="truncate">{wo.part_internal_no}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Unassigned Row */}
                  {filteredPlanningData.unassigned?.length > 0 && (
                    <div className="flex border-b bg-orange-50">
                      <div className="w-48 flex-shrink-0 p-2 border-r">
                        <div className="font-medium text-sm text-orange-600">Unassigned</div>
                        <div className="text-xs text-muted-foreground">{filteredPlanningData.unassigned.length} WOs</div>
                      </div>
                      <div className="flex-1 p-2 flex flex-wrap gap-2">
                        {filteredPlanningData.unassigned.slice(0, 10).map((wo) => (
                          <Badge key={wo.id} variant="outline" className="text-xs">
                            {wo.wo_number}
                          </Badge>
                        ))}
                        {filteredPlanningData.unassigned.length > 10 && (
                          <Badge variant="secondary" className="text-xs">+{filteredPlanningData.unassigned.length - 10} more</Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {machines.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No machines configured. Add machines in Master Data first.
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Work Orders List Tab */}
        <TabsContent value="work-orders" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" /> All Work Orders
              </CardTitle>
              <CardDescription>Complete list of production work orders</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-4 items-center justify-between">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search work orders..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="showCompletedList" 
                    checked={showCompleted} 
                    onCheckedChange={setShowCompleted}
                  />
                  <Label htmlFor="showCompletedList" className="text-sm cursor-pointer">
                    Show Completed
                  </Label>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : filteredWorkOrders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {showCompleted ? 'No work orders found.' : 'No active work orders. Check "Show Completed" to view history.'}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>WO #</TableHead>
                      <TableHead>Part No</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Machine</TableHead>
                      <TableHead className="text-right">Target Qty</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredWorkOrders.map((wo) => {
                      const isDeliveryLate = wo.target_delivery_date && wo.planned_end_date && 
                        new Date(wo.planned_end_date) > new Date(wo.target_delivery_date);
                      
                      return (
                        <TableRow key={wo.id} className={`${wo.status === 'completed' ? 'opacity-60' : ''} ${isDeliveryLate ? 'bg-red-50' : ''}`}>
                          <TableCell className="font-mono font-medium">
                            <div className="flex items-center gap-1">
                              {isDeliveryLate && <AlertTriangle className="h-4 w-4 text-red-600" title="Late Delivery Warning" />}
                              {wo.wo_number}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono">{wo.part_internal_no || wo.part_description}</TableCell>
                          <TableCell>{wo.customer_name || '-'}</TableCell>
                          <TableCell>
                            {/* Inline machine assignment - editable for draft WOs */}
                            {wo.status === 'draft' && !wo.assigned_machine_id ? (
                              <div className="flex items-center gap-2">
                                {updatingWoId === wo.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Select 
                                    value="" 
                                    onValueChange={(v) => handleInlineMachineAssign(wo, v)}
                                  >
                                    <SelectTrigger className="w-[160px] h-8 text-xs border-amber-300 bg-amber-50">
                                      <SelectValue placeholder="Assign Machine" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {machines.map(m => (
                                        <SelectItem key={m.id} value={m.id} className="text-xs">
                                          {m.name} ({m.process_type})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                                {wo.is_manual && (
                                  <Badge variant="outline" className="text-xs text-amber-600 border-amber-400">Manual</Badge>
                                )}
                              </div>
                            ) : wo.status === 'draft' && wo.assigned_machine_id ? (
                              <div className="flex items-center gap-2">
                                <Select 
                                  value={wo.assigned_machine_id} 
                                  onValueChange={(v) => handleInlineMachineAssign(wo, v)}
                                  disabled={updatingWoId === wo.id}
                                >
                                  <SelectTrigger className="w-[160px] h-8 text-xs">
                                    <SelectValue>{wo.machine_name}</SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {machines.map(m => (
                                      <SelectItem key={m.id} value={m.id} className="text-xs">
                                        {m.name} ({m.process_type})
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            ) : (
                              wo.machine_name || <span className="text-muted-foreground italic">Unassigned</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono">{wo.qty_target?.toLocaleString()}</TableCell>
                          <TableCell>{formatDate(wo.planned_start_date)}</TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[wo.status]}>{wo.status?.toUpperCase()}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={PRIORITY_LABELS[wo.priority]?.color}>
                              {PRIORITY_LABELS[wo.priority]?.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {wo.status === 'draft' && (
                                <Button size="sm" variant="outline" onClick={() => handleReleaseWO(wo)}>
                                  <Play className="h-4 w-4 mr-1" /> Release
                                </Button>
                              )}
                              {wo.status === 'released' && (
                                <Button size="sm" variant="outline" className="text-yellow-600" onClick={() => handleStartWO(wo)}>
                                  <Clock className="h-4 w-4 mr-1" /> Start
                                </Button>
                              )}
                              {wo.status === 'wip' && (
                                <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleCompleteWO(wo)}>
                                  <Check className="h-4 w-4 mr-1" /> Complete
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => window.open(`/work-orders/${wo.id}/print`, '_blank')}>
                                <Printer className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Material Planning Tab */}
        <TabsContent value="materials" className="space-y-4">
          <MaterialPlanningTab />
        </TabsContent>
      </Tabs>

      {/* Create Work Order Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Work Order</DialogTitle>
          </DialogHeader>
          
          {selectedBacklogItem && (
            <div className="space-y-4">
              {/* Source Info */}
              <Card className="bg-muted/50">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><strong>Order:</strong> {selectedBacklogItem.order_number}</div>
                    <div><strong>Customer:</strong> {selectedBacklogItem.customer_name}</div>
                    <div><strong>Part:</strong> {selectedBacklogItem.part_internal_no}</div>
                    <div><strong>Remaining Qty:</strong> {selectedBacklogItem.remaining_qty?.toLocaleString()}</div>
                  </div>
                </CardContent>
              </Card>

              {/* Quantity & Schedule */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Target Quantity *</Label>
                  <Input
                    type="number"
                    value={woForm.qty_target}
                    onChange={(e) => setWoForm(p => ({ ...p, qty_target: parseFloat(e.target.value) || 0 }))}
                    max={selectedBacklogItem.remaining_qty}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Planned Start</Label>
                  <Input
                    type="date"
                    value={woForm.planned_start_date}
                    onChange={(e) => setWoForm(p => ({ ...p, planned_start_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Planned End</Label>
                  <Input
                    type="date"
                    value={woForm.planned_end_date}
                    onChange={(e) => setWoForm(p => ({ ...p, planned_end_date: e.target.value }))}
                  />
                </div>
              </div>

              {/* Machine & Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Assigned Machine</Label>
                  <Select 
                    value={woForm.assigned_machine_id} 
                    onValueChange={handleMachineChange}
                    data-testid="machine-select"
                  >
                    <SelectTrigger><SelectValue placeholder="Select machine..." /></SelectTrigger>
                    <SelectContent>
                      {machines.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name} ({m.process_type})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Selecting a machine will auto-fill the start date based on availability
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={String(woForm.priority)} onValueChange={(v) => setWoForm(p => ({ ...p, priority: parseInt(v) }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Critical</SelectItem>
                      <SelectItem value="2">2 - Normal</SelectItem>
                      <SelectItem value="3">3 - Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Technical Specs */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Technical Specifications (for Traveler)</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>RM Grade</Label>
                    <Input
                      value={woForm.rm_grade}
                      onChange={(e) => setWoForm(p => ({ ...p, rm_grade: e.target.value }))}
                      placeholder="e.g., SWRM 10A"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>RM Size</Label>
                    <Input
                      value={woForm.rm_size}
                      onChange={(e) => setWoForm(p => ({ ...p, rm_size: e.target.value }))}
                      placeholder="e.g., 10.2 mm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Wire Size (After Drawing)</Label>
                    <Input
                      value={woForm.wire_size}
                      onChange={(e) => setWoForm(p => ({ ...p, wire_size: e.target.value }))}
                      placeholder="e.g., 9.8 mm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label>RM Qty Required (Kg)</Label>
                    <Input
                      type="number"
                      value={woForm.rm_qty_required}
                      onChange={(e) => setWoForm(p => ({ ...p, rm_qty_required: e.target.value }))}
                      placeholder="e.g., 250"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Supply Condition</Label>
                    <Select value={woForm.supply_condition} onValueChange={(v) => setWoForm(p => ({ ...p, supply_condition: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {SUPPLY_CONDITIONS.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Header Tool Status</Label>
                    <Select value={woForm.header_tool_status} onValueChange={(v) => setWoForm(p => ({ ...p, header_tool_status: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {TOOL_STATUS_OPTIONS.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label>Heat Treatment</Label>
                    <Input
                      value={woForm.heat_treatment}
                      onChange={(e) => setWoForm(p => ({ ...p, heat_treatment: e.target.value }))}
                      placeholder="e.g., Case Hardened"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Surface Finish</Label>
                    <Input
                      value={woForm.surface_finish}
                      onChange={(e) => setWoForm(p => ({ ...p, surface_finish: e.target.value }))}
                      placeholder="e.g., Zinc Plated"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Production Notes</Label>
                <Input
                  value={woForm.production_notes}
                  onChange={(e) => setWoForm(p => ({ ...p, production_notes: e.target.value }))}
                  placeholder="Special instructions for production..."
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateWO} disabled={creating}>
              {creating ? 'Creating...' : 'Create Work Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Material Planning Tab Component
function MaterialPlanningTab() {
  const [requirements, setRequirements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  const fetchRequirements = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await reportsAPI.rmRequirements(params);
      setRequirements(res.data.requirements || []);
      setSummary(res.data.summary || null);
    } catch (error) {
      toast.error('Failed to fetch RM requirements');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchRequirements();
  }, [fetchRequirements]);

  const toggleRow = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const exportToExcel = () => {
    // Create CSV data
    const headers = ['RM Grade', 'Wire Size', 'Total Weight (Kg)', 'Order Count', 'Total Qty'];
    const rows = requirements.map(r => [
      r.rm_grade,
      r.wire_size,
      r.total_weight_kg,
      r.order_count,
      r.total_qty
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rm_requirements_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  const printReport = () => {
    window.open('/production/rm-report', '_blank');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" /> Raw Material Requirements
            </CardTitle>
            <CardDescription>Aggregated RM needs from Released and WIP Work Orders</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Target Date:</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-36"
                placeholder="From"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-36"
                placeholder="To"
              />
            </div>
            <Button variant="outline" size="sm" onClick={exportToExcel}>
              <FileSpreadsheet className="mr-2 h-4 w-4" /> Export
            </Button>
            <Button variant="outline" size="sm" onClick={printReport}>
              <Printer className="mr-2 h-4 w-4" /> Print PDF
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : requirements.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No RM requirements found</p>
            <p className="text-sm">Release Work Orders to generate material requirements</p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="bg-primary/5">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-primary">{summary.total_weight_kg?.toLocaleString()} Kg</div>
                    <div className="text-xs text-muted-foreground">Total Weight Required</div>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-blue-600">{summary.total_orders}</div>
                    <div className="text-xs text-muted-foreground">Work Orders</div>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-50">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-emerald-600">{summary.total_qty?.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">Total Quantity</div>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-purple-600">{summary.unique_materials}</div>
                    <div className="text-xs text-muted-foreground">Unique Materials</div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Requirements Table */}
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead className="w-10"></TableHead>
                  <TableHead>RM Grade</TableHead>
                  <TableHead>Wire Size</TableHead>
                  <TableHead className="text-right">Total Weight (Kg)</TableHead>
                  <TableHead className="text-right">Order Count</TableHead>
                  <TableHead className="text-right">Total Qty</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requirements.map((req, idx) => (
                  <React.Fragment key={req.rm_grade || idx}>
                    <TableRow 
                      className="table-row cursor-pointer hover:bg-muted/50"
                      onClick={() => toggleRow(idx)}
                    >
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                          {expandedRows.has(idx) ? '−' : '+'}
                        </Button>
                      </TableCell>
                      <TableCell className="font-medium">{req.rm_grade}</TableCell>
                      <TableCell>{req.wire_size}</TableCell>
                      <TableCell className="text-right font-mono font-bold text-primary">
                        {req.total_weight_kg?.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">{req.order_count}</TableCell>
                      <TableCell className="text-right font-mono">{req.total_qty?.toLocaleString()}</TableCell>
                    </TableRow>
                    {/* Expanded Detail Row */}
                    {expandedRows.has(idx) && req.work_orders && (
                      <TableRow>
                        <TableCell colSpan={6} className="bg-muted/30 p-4">
                          <div className="text-sm font-medium mb-2">Work Orders:</div>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>WO #</TableHead>
                                <TableHead>Part</TableHead>
                                <TableHead className="text-right">Qty</TableHead>
                                <TableHead className="text-right">RM Qty (Kg)</TableHead>
                                <TableHead>Target Date</TableHead>
                                <TableHead>Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {req.work_orders.map((wo) => (
                                <TableRow key={wo.id || wo.wo_number}>
                                  <TableCell className="font-mono">{wo.wo_number}</TableCell>
                                  <TableCell className="font-mono text-sm">{wo.part_internal_no}</TableCell>
                                  <TableCell className="text-right">{wo.qty_target?.toLocaleString()}</TableCell>
                                  <TableCell className="text-right font-mono">{wo.rm_qty_required?.toLocaleString() || '-'}</TableCell>
                                  <TableCell>{wo.planned_end_date ? new Date(wo.planned_end_date).toLocaleDateString() : '-'}</TableCell>
                                  <TableCell>
                                    <Badge className={`${wo.status === 'wip' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
                                      {wo.status?.toUpperCase()}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
}

